'use client';

import { API_ROUTES, useApiSuspenseQuery } from '@shared/api-utils';
import { createDebug } from '@shared/debug';
import { useClientConfig } from '@shared/utils';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { SmallLogo } from '../../../../Logos/Logos';
import { FALLBACK_OFFER_IMAGE } from './constants';
import { OfferPrimaryFallback } from './OfferPrimaryFallback';
import type { LandingPodData, OfferDisplayData, OfferPrimaryContentProps, OfferRegistrationData } from './types';

const debug = createDebug({ isEnabled: false });

const mapToOfferDisplayData = (data: OfferRegistrationData | LandingPodData, baseImgUrl: string): OfferDisplayData => {
  if ('bundleCode' in data) {
    return {
      title: data.title,
      expiresAt: data.expirationDate,
      imageSrc: data.images?.desktop ? baseImgUrl + data.images?.desktop : null,
      imageAlt: 'Offer visual',
      bundleCode: data.bundleCode,
    };
  }

  return {
    title: data.description, // the description is the title of the landing pods API
    callout: data.title,
    expiresAt: data.expiresAt,
    imageSrc: data.image?.link ?? null,
    imageAlt: data.image?.altText ?? 'Offer visual',
    bundleCode: undefined,
  };
};

/**
 * Primary offer shown on the left side of every step of the registration form on desktop, and on the first step on mobile
 *
 * Behaviour:
 * - Reads the `bundleCode` from URL search params
 * - If missing, immediately renders `OfferPrimaryFallback` to avoid any API call
 * - Otherwise builds the offer API path and delegates data fetching/rendering to `OfferPrimaryContent`
 * - Suspense for loading states is expected to be handled by the parent (e.g. `RegistrationForm`)
 */
function OfferPrimary() {
  const searchParams = useSearchParams();
  const landingPageId = searchParams.get('lpid') || '';
  const bundleCode = searchParams.get('code') || '';
  const offerPath = bundleCode ? API_ROUTES.OFFER_BUNDLES.PUBLIC.replace(':offerbundleCode', bundleCode.toUpperCase()) : '';
  const landingPodPath = API_ROUTES.LANDING_PODS.replace(':slug', landingPageId);

  if (!landingPageId && !bundleCode) {
    return <OfferPrimaryFallback />;
  }

  if (landingPageId) {
    return <OfferPrimaryContent landingPodPath={landingPodPath} landingPageId={landingPageId} />;
  }

  return <OfferPrimaryContent offerPath={offerPath} bundleCode={bundleCode.toUpperCase()} />;
}

/**
 * - Fetches offer details for `offerPath` with React Query in Suspense mode
 * - Uses `returnNullOn404` and `returnNullOn400` to treat 404/400 as "no offer" rather than an error
 * - When data is null (404 or 400), renders `OfferPrimaryFallback` instead of throwing
 * - Disables retries (`retry: false`) to avoid repeated failing calls
 * - When data exists, renders the offer UI (title, expiration date, image, etc.)
 */
function OfferPrimaryContent({ offerPath, landingPodPath, landingPageId, bundleCode }: OfferPrimaryContentProps) {
  const path = (offerPath ?? landingPodPath) as string;
  const { data, error, isLoading } = useApiSuspenseQuery<OfferRegistrationData | LandingPodData | null>(path, {
    returnNullOn404: true,
    returnNullOn400: true,
    queryOptions: {
      retry: false,
    },
  });

  const t = useTranslations('register');
  const config = useClientConfig();

  useEffect(() => {
    if (!data && bundleCode && !isLoading) {
      debug.forceError('[OFFER BUNDLE] Fallback shown due to 400 or 404 API error', { route: offerPath, code: bundleCode });
    }
  }, [data, bundleCode, offerPath, isLoading]);

  if (!data || error) {
    return <OfferPrimaryFallback />;
  }

  const { title, expiresAt, imageSrc, imageAlt, bundleCode: displayBundleCode, callout } = mapToOfferDisplayData(data, config.baseImgUrl);

  return (
    <div
      className="flex flex-col md:justify-center h-full pt-10 pb-12 px-6 md:p-20 md:pr-15 bg-foreground dark:bg-container-base text-foreground-inverse dark:text-background-inverse"
      data-testid="offer-primary"
      data-track-container="Value Proposition"
      data-track-offer={landingPageId || displayBundleCode}
    >
      {/* Logo - Using Lottoland logo as per requirements */}
      <div className="mb-5 md:mb-8">
        <div className="relative w-12 h-10 md:w-16 md:h-13">
          <SmallLogo className="w-full h-full text-accent-green" data-testid="offer-logo" />
        </div>
      </div>

      {/* Text Content */}
      <div className="my-2">
        <p className="paragraph-strong text-accent-green" data-testid="offer-title">
          {callout ? callout : t('offer.primary.title')}
        </p>
        {title ? (
          <h5 className="md:heading-3" data-testid="offer-description">
            {title}
          </h5>
        ) : null}
        {expiresAt ? (
          <div className="inline-block mt-5 py-1 px-2 bg-background/10 dark:bg-foreground/10 rounded-md">
            <p className="paragraph-s-strong" data-testid="offer-valid-until" data-expiry-date={expiresAt}>
              {t('offer.primary.valid_until', { date: new Date(expiresAt) })}
            </p>
          </div>
        ) : null}
      </div>

      {/* Image - hidden on mobile, only desktop image used */}
      <div className="hidden md:block relative max-w-132.5 w-full aspect-[530/280] rounded-2xl mt-10 md:mt-20 mx-auto">
        <Image
          src={imageSrc ? `${imageSrc}` : FALLBACK_OFFER_IMAGE}
          alt={imageAlt}
          fill
          className="rounded-2xl object-cover"
          sizes="(max-width: 768px) 100vw, 530px"
          priority
          unoptimized
          data-testid="offer-image"
        />
      </div>
    </div>
  );
}

export { OfferPrimary };
